import { request } from './request';

export interface TargetingCriteria {
  service_types?: number[];
  locations?: number[];
  min_rating?: number;
  verified_only?: boolean;
  min_projects_completed?: number;
  industries?: string[];
  languages?: string[];
  availability?: 'available' | 'busy' | 'any';
  price_range?: { min?: number; max?: number };
}

export interface Broadcast {
  id: string;
  title: string;
  content: string;
  target_audience: 'all' | 'clients' | 'filmmakers' | 'both' | 'individual' | 'selected' | 'criteria';
  target_user_id?: string;
  target_user_role?: 'client' | 'filmmaker';
  targeting_criteria?: TargetingCriteria;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  created_at: string;
  created_by: string;
  expires_at?: string;
  is_active: boolean;
  is_read?: boolean;
  read_at?: string | null;
  metadata?: any;
  broadcast_recipients?: Array<{
    user_id: string;
    target_role?: 'client' | 'filmmaker';
    users?: {
      first_name: string;
      last_name: string;
      email: string;
    };
  }>;
}

export interface CreateBroadcastRequest {
  title: string;
  content: string;
  target_audience: 'all' | 'clients' | 'filmmakers' | 'both' | 'individual' | 'selected' | 'criteria';
  target_user_id?: string;
  target_user_role?: 'client' | 'filmmaker';
  recipients?: Array<{
    user_id: string;
    target_role?: 'client' | 'filmmaker';
  }>;
  targeting_criteria?: TargetingCriteria;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  expires_at?: string;
  metadata?: any;
}

export interface BroadcastPreviewResponse {
  total_recipients: number;
  recipients: Array<{
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
  }>;
  criteria_summary: TargetingCriteria | null;
}

export interface TestBroadcastResponse {
  success: boolean;
  test_broadcast: {
    id: string;
    title: string;
    content: string;
    target_audience: string;
    priority: string;
    created_at: string;
    created_by: string;
    is_test: boolean;
    test_recipient: string;
  };
  message: string;
}

export interface UserSearchResult {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  user_type_id: number;
  user_types: {
    user_type_name: string;
  };
  hasClient: boolean;
  hasFilmmaker: boolean;
}

export const broadcastsApi = {
  // Get all broadcasts (admin)
  getBroadcasts: () => 
    request<Broadcast[]>('/broadcasts'),

  // Get broadcasts for current user
  getMyBroadcasts: (viewingRole?: 'client' | 'filmmaker' | 'both') =>
    request<Broadcast[]>(`/broadcasts/my-broadcasts${viewingRole ? `?role=${viewingRole}` : ''}`),

  // Create new broadcast
  createBroadcast: (data: CreateBroadcastRequest) =>
    request<Broadcast>('/broadcasts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Preview broadcast recipients
  previewBroadcast: (data: CreateBroadcastRequest) =>
    request<BroadcastPreviewResponse>('/broadcasts/preview', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Send test broadcast (dry run)
  testBroadcast: (data: CreateBroadcastRequest) =>
    request<TestBroadcastResponse>('/broadcasts/test', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Update broadcast (e.g., deactivate)
  updateBroadcast: (id: string, data: Partial<Broadcast>) =>
    request<Broadcast>(`/broadcasts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Delete broadcast
  deleteBroadcast: (id: string) =>
    request<{ success: boolean }>(`/broadcasts/${id}`, {
      method: 'DELETE',
    }),

  // Search users for individual targeting
  searchUsers: (query: string) =>
    request<UserSearchResult[]>(`/broadcasts/search-users?query=${encodeURIComponent(query)}`),

  // Mark broadcast as viewed/read
  markBroadcastAsViewed: (broadcastId: string) =>
    request<{ success: boolean }>(`/broadcasts/${broadcastId}/view`, {
      method: 'POST',
    }),
};